// Each card is its own drop target carrying a closest edge; the target slot comes from
// `getReorderDestinationIndex`, and the slide-apart gap is derived from the source/target indices only.
// A reveal box is a nested drop target carrying its own ordered member ids, so the same index-only model
// resolves drops inside it without any shared coordinate space.
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';

import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { attachClosestEdge, extractClosestEdge, type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

// A drop resolves to one of these outcomes. The flat-canvas pair (`create`/`reorder`) is joined by the
// reveal-box variants: create a field inside a rule's box, move a canvas card in, move a revealed card back
// out to the canvas, or reorder within the box. All `dependsOn` writes are done by the consumer's reconciler.
export type DesignerDrop =
  | { kind: 'create'; label: string; targetIndex: number }
  | { kind: 'reorder'; fromIndex: number; toIndex: number }
  | { kind: 'create-in-reveal'; label: string; triggerId: string; ruleId: string; toIndex: number }
  | { kind: 'move-to-reveal'; id: string; triggerId: string; ruleId: string; toIndex: number }
  | { kind: 'move-to-canvas'; id: string; toIndex: number }
  | { kind: 'reorder-in-reveal'; triggerId: string; ruleId: string; fromIndex: number; toIndex: number };

const SETTLE_EASING = 'cubic-bezier(0.2, 0, 0, 1)';
// The inter-card spacing each card wrapper carries as `pb-2` (Tailwind spacing 2 = 8px). A reorder shift is
// the dragged card's wrapper height (spacing already included); a create opens a gap from the toolbox row,
// which has no wrapper, so this restores the full tiled-slot pitch. Keep in sync with the wrapper's `pb-2`.
const CARD_WRAPPER_SPACING = 8;

// Floor for the reveal box's reserved incoming-card slot — a toolbox item's drag height is its small sidebar
// row, far shorter than the field card it becomes, so the box must grow by at least a card's worth.
const MIN_REVEAL_SLOT_HEIGHT = 64;

// Drag payload on Pragmatic's source.data. `preview` is the React node the floating overlay renders.
// `revealGroup` is set (to `triggerId:ruleId`) when the card currently lives inside a reveal box, so a drop
// can tell same-box reorder from a move between contexts. `isTrigger` marks a card that carries a rule — the
// monitor can't look the element up, so a reveal box reads this to refuse a trigger (would orphan its children).
type ToolboxDragData = { kind: 'toolbox'; label: string; preview: ReactNode };
type CardDragData = { kind: 'card'; id: string; preview: ReactNode; revealGroup?: string; isTrigger?: boolean };
// Drop-target data a card registers; the closest edge is attached onto it at hit-test time.
type CardDropData = { kind: 'card'; id: string };
// Drop-target data on the dashed reveal box (a nested dropTargetForElements inside a trigger card). It carries
// the box's ordered member ids so the monitor resolves the landing slot against them — no DOM geometry needed.
type RevealDropData = { kind: 'reveal'; triggerId: string; ruleId: string; memberIds: string[] };

const isCard = (data: Record<string, unknown>): data is CardDragData => data.kind === 'card';
const isToolbox = (data: Record<string, unknown>): data is ToolboxDragData => data.kind === 'toolbox';
const isCardDropData = (data: Record<string, unknown>): data is CardDropData => data.kind === 'card';
const isReveal = (data: Record<string, unknown>): data is RevealDropData => data.kind === 'reveal';

// The reveal group id a card/box belongs to — `triggerId:ruleId`, or undefined for the flat canvas.
const revealGroupId = (triggerId: string, ruleId: string): string => `${triggerId}:${ruleId}`;

// Whether a reveal box will accept a drop, decided up front at hit-test time so a box that will reject neither
// lights up nor reserves a slot (and then no-ops on release). A non-card source (toolbox item) is always
// allowed; a card is rejected on self-reveal (dropped on its own trigger) or when the guard forbids the nesting
// (ancestor cycle / over-cap). `guard` is undefined when the feature flag is off → allow. Pure so it can be
// unit-tested; the monitor's onDrop re-checks the same guard as the authoritative commit gate.
export function canDropCardInRevealBox(args: {
  source: Record<string, unknown>;
  triggerId: string;
  guard: ((sourceId: string, triggerId: string) => boolean) | undefined;
}): boolean {
  const { source, triggerId, guard } = args;
  if (!isCard(source)) return true;
  if (source.id === triggerId) return false;
  return guard?.(source.id, triggerId) ?? true;
}

// Signed displacement per card id: −1 = shift up one slot, +1 = shift down. The dragged card holds its
// origin slot (visibility:hidden, in flow), so the list height never changes on lift.
export type Displacement = Map<string, -1 | 1>;

// Which cards slide, and in which direction, to open the gap at `targetIndex`. Derived purely from the two
// indices — no geometry — so a nested list (a reveal box) can call this with its own ids without any shared
// coordinate space. A create (sourceIndex === null) shifts the tail from targetIndex down to open a new slot;
// a reorder moves the single gap between source and target.
export function computeShift(orderedIds: string[], sourceIndex: number | null, targetIndex: number): Displacement {
  const displacement: Displacement = new Map();
  if (sourceIndex === null) {
    for (let i = targetIndex; i < orderedIds.length; i++) displacement.set(orderedIds[i], 1);
    return displacement;
  }
  if (targetIndex > sourceIndex) {
    // Clamp to the last real index: a reorder to the tail passes targetIndex === orderedIds.length, and
    // orderedIds[length] is undefined — including it would emit a displacement keyed on `undefined`.
    const lastShifted = Math.min(targetIndex, orderedIds.length - 1);
    for (let i = sourceIndex + 1; i <= lastShifted; i++) displacement.set(orderedIds[i], -1);
  } else if (targetIndex < sourceIndex) {
    for (let i = targetIndex; i < sourceIndex; i++) displacement.set(orderedIds[i], 1);
  }
  return displacement;
}

const sameDisplacement = (a: Displacement, b: Displacement): boolean => {
  if (a.size !== b.size) return false;
  for (const [id, dir] of a) if (b.get(id) !== dir) return false;
  return true;
};

// Probe offset for a card sitting BELOW the dragged source in its ordered list: shift the probe down by the
// dragged height so the dragged card's bottom (leading) edge drives the hand-over on a downward drag — a
// neighbour below hands over as the dragged bottom edge crosses its midpoint, not its top. The flat canvas and
// a reveal box reduce to the same rule; only the ordered list differs (flat `orderedCardIds` vs box memberIds).
export function leadingEdgeProbeAdjust(cardIndexInList: number, sourceIndexInList: number | null, draggedHeight: number): number {
  return sourceIndexInList !== null && cardIndexInList > sourceIndexInList ? draggedHeight : 0;
}

// The slot a drop lands on, from the hovered card's id + its closest edge. `top` edge → that card's index;
// `bottom` → one past it. Works for both the flat canvas and a reveal box (pass that list's ids). Only called
// for a real card target; a container/box hit is handled by the caller.
export function targetIndexFromEdge(orderedIds: string[], targetId: string, edge: Edge | null): number {
  const indexOfTarget = orderedIds.indexOf(targetId);
  if (indexOfTarget === -1) return orderedIds.length;
  return edge === 'bottom' ? indexOfTarget + 1 : indexOfTarget;
}

// The card slot a hover over a reveal box targets. The innermost hit `targets[0]` can be the trigger card's own
// drop target (an ANCESTOR wrapping the box), or a displaced member that dropped out of hit-testing — in both
// cases the hovered `kind:'card'` isn't one of the box's members. Scan for the innermost card whose id IS a
// member, so a non-member hit resolves to null (→ append at the end) instead of mis-targeting the trigger.
export function pickRevealTargetCard(dropTargets: readonly { data: Record<string, unknown> }[], memberIds: string[]): { id: string; edge: Edge | null } | null {
  const member = dropTargets.find((target) => isCardDropData(target.data) && memberIds.includes(target.data.id));
  if (!member || !isCardDropData(member.data)) return null;
  return { id: member.data.id, edge: extractClosestEdge(member.data) };
}

// Decides which drop outcome a release maps to, from the innermost-first dropTargets stack + the source. Pure
// so it can be unit-tested (JSDOM can't drive a real drag). The landing indices are pre-computed by the
// monitor: `flatTargetIndex`/`revealTargetIndex` from the closest edge against the flat / box ids;
// `fromFlatIndex`/`fromRevealIndex` are the dragged card's current position in each context (−1 when absent).
export function resolveDropTarget(args: {
  dropTargets: readonly { data: Record<string, unknown> }[];
  source: Record<string, unknown>;
  flatTargetIndex: number;
  revealTargetIndex: number;
  fromFlatIndex: number;
  fromRevealIndex: number;
  // Precomputed verdict for dropping `source` into the box under the pointer: false when the drop would form
  // a cycle (source is the box trigger or an ancestor of it) or push the moved subtree past the depth cap.
  canDropCardInReveal: boolean;
}): DesignerDrop | null {
  const { dropTargets, source, flatTargetIndex, revealTargetIndex, fromFlatIndex, fromRevealIndex, canDropCardInReveal } = args;
  if (dropTargets.length === 0) return null; // dropped outside any target

  // A revealed card is the innermost hit, so the reveal box sits deeper in the stack — scan, don't take [0].
  const reveal = dropTargets.map((t) => t.data).find(isReveal) ?? null;

  if (reveal) {
    const group = revealGroupId(reveal.triggerId, reveal.ruleId);
    if (isToolbox(source)) {
      return { kind: 'create-in-reveal', label: source.label, triggerId: reveal.triggerId, ruleId: reveal.ruleId, toIndex: revealTargetIndex };
    }
    if (isCard(source)) {
      // A card already in THIS box just reorders — the cross-box cycle/depth verdict doesn't apply.
      if (source.revealGroup === group) {
        return fromRevealIndex !== -1 && fromRevealIndex !== revealTargetIndex
          ? { kind: 'reorder-in-reveal', triggerId: reveal.triggerId, ruleId: reveal.ruleId, fromIndex: fromRevealIndex, toIndex: revealTargetIndex }
          : null;
      }
      // Moving a card into a different box: the verdict rejects self-reveal, ancestor cycles, and over-cap
      // nesting (a rule-carrying card carries its subtree, so this is also where nesting a trigger lands).
      if (!canDropCardInReveal) return null;
      return { kind: 'move-to-reveal', id: source.id, triggerId: reveal.triggerId, ruleId: reveal.ruleId, toIndex: revealTargetIndex };
    }
    return null;
  }

  // No reveal target under the pointer → the flat canvas.
  if (isToolbox(source)) {
    return { kind: 'create', label: source.label, targetIndex: flatTargetIndex };
  }
  if (isCard(source)) {
    if (source.revealGroup) return { kind: 'move-to-canvas', id: source.id, toIndex: flatTargetIndex }; // dragged OUT
    return fromFlatIndex !== -1 && fromFlatIndex !== flatTargetIndex ? { kind: 'reorder', fromIndex: fromFlatIndex, toIndex: flatTargetIndex } : null;
  }
  return null;
}

type OverlayState = { content: ReactNode; x: number; y: number; offsetX: number; offsetY: number; width: number } | null;

const DndStateContext = createContext<{
  displacement: Displacement;
  draggedId: string | null;
  draggedHeight: number;
  isDropping: boolean;
  // Refs each card's getData reads at hit-test time to project the probe to the dragged card's leading edge
  // (top edge for neighbours above the source and for a create, bottom edge for neighbours below).
  grabOffsetYRef: { current: number };
  draggedHeightRef: { current: number };
  sourceIndexRef: { current: number | null };
  orderedIdsRef: { current: string[] };
  // The cycle/depth verdict, so a box that will reject the drop can also refuse the hover affordance up front
  // (RevealDropZone.canDrop) instead of lighting up and then no-opping on release. Undefined → allow (flag off).
  guardRef: { current: ((sourceId: string, triggerId: string) => boolean) | undefined };
}>({
  displacement: new Map(),
  draggedId: null,
  draggedHeight: 0,
  isDropping: false,
  grabOffsetYRef: { current: 0 },
  draggedHeightRef: { current: 0 },
  sourceIndexRef: { current: null },
  orderedIdsRef: { current: [] },
  guardRef: { current: undefined },
});

// Mounts the central monitor + the floating drag overlay; wraps the whole designer edit area.
export function FormDesignerDndProvider({
  children,
  onDrop,
  orderedCardIds,
  revealDropGuard,
}: {
  children: ReactNode;
  onDrop: (drop: DesignerDrop) => void;
  orderedCardIds: string[]; // FLAT canvas card ids in order (revealed cards excluded) — indices resolve here
  // Whether a card may be dropped into a given box: false for a cycle (source is the box trigger or an
  // ancestor of it) or an over-cap nesting. Omitted → every cross-box drop is allowed (feature flag off).
  revealDropGuard?: (sourceId: string, triggerId: string) => boolean;
}) {
  const [displacement, setDisplacement] = useState<Displacement>(new Map());
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [draggedHeight, setDraggedHeight] = useState(0);
  const [overlay, setOverlay] = useState<OverlayState>(null);
  // True only for the drop commit: displaced cards clear their transform in the same paint as the reorder,
  // so suppressing their transition for that frame stops the top-neighbour flicker. Cleared next frame.
  const [isDropping, setIsDropping] = useState(false);

  const sourceIndexRef = useRef<number | null>(null);
  // The resolved landing index in each context, updated per frame by onDrag and read by onDrop.
  const flatTargetIndexRef = useRef(0);
  const revealTargetIndexRef = useRef(0);
  const fromRevealIndexRef = useRef(-1); // dragged card's index in the box it's over (−1 = not over a box)
  const grabOffsetYRef = useRef(0);
  const draggedHeightRef = useRef(0);
  const displacementRef = useRef<Displacement>(new Map());
  // Latest values available to the monitor without re-subscribing it on every render.
  const orderedIdsRef = useRef(orderedCardIds);
  orderedIdsRef.current = orderedCardIds;
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;
  const guardRef = useRef(revealDropGuard);
  guardRef.current = revealDropGuard;

  // `committed` = a reorder/create actually fired. Only then suppress the settle transition, so the displaced
  // cards snap to 0 in the same paint as the reorder; a cancel or drop-outside eases them back instead.
  const resetDragState = (committed = false) => {
    if (committed && displacementRef.current.size > 0) {
      setIsDropping(true);
      requestAnimationFrame(() => setIsDropping(false));
    }
    setOverlay(null);
    setDraggedId(null);
    setDraggedHeight(0);
    setDisplacement(new Map());
    displacementRef.current = new Map();
    sourceIndexRef.current = null;
    fromRevealIndexRef.current = -1;
  };

  const applyDisplacement = (next: Displacement) => {
    if (!sameDisplacement(next, displacementRef.current)) {
      displacementRef.current = next;
      setDisplacement(next);
    }
  };

  useEffect(() => {
    const cleanup = monitorForElements({
      canMonitor: ({ source }) => {
        const data = source.data as Record<string, unknown>;
        return isCard(data) || isToolbox(data);
      },
      onDragStart({ source, location }) {
        const data = source.data as Record<string, unknown>;
        const rect = source.element.getBoundingClientRect();
        const input = location.current.input;
        const activeId = isCard(data) ? data.id : null;
        // A revealed card isn't in the flat list, so its flat source index is null — it enters the canvas as
        // an incoming card (and the reveal branch tracks its box index separately).
        const sourceIndex = activeId ? orderedIdsRef.current.indexOf(activeId) : -1;
        sourceIndexRef.current = sourceIndex === -1 ? null : sourceIndex;
        // Where on the dragged element the pointer grabbed; the probe is offset by this so the edge decision
        // tracks the dragged card's leading edge, not the raw pointer.
        grabOffsetYRef.current = input.clientY - rect.top;

        // Displaced cards shift by one tiled-slot pitch. A reorder uses the dragged card's wrapper height
        // (its `pb-2` already included); a create drags the toolbox row (no wrapper), so add the spacing to
        // match the slot the inserted card will occupy.
        const slotPitch = activeId ? rect.height : rect.height + CARD_WRAPPER_SPACING;
        draggedHeightRef.current = slotPitch;

        flatTargetIndexRef.current = sourceIndexRef.current ?? orderedIdsRef.current.length;
        revealTargetIndexRef.current = 0;
        fromRevealIndexRef.current = -1;
        setDraggedId(activeId);
        setDraggedHeight(slotPitch);
        setOverlay({
          content: isCard(data) || isToolbox(data) ? data.preview : null,
          x: input.clientX,
          y: input.clientY,
          offsetX: input.clientX - rect.left,
          offsetY: input.clientY - rect.top,
          width: rect.width,
        });
      },
      onDrag({ source, location }) {
        const input = location.current.input;
        setOverlay((prev) => (prev ? { ...prev, x: input.clientX, y: input.clientY } : prev));

        // No drop target under the pointer (e.g. over the toolbox sidebar) → no gap.
        if (location.current.dropTargets.length === 0) {
          flatTargetIndexRef.current = sourceIndexRef.current ?? orderedIdsRef.current.length;
          fromRevealIndexRef.current = -1;
          revealTargetIndexRef.current = 0;
          applyDisplacement(new Map());
          return;
        }

        const targets = location.current.dropTargets;
        const sourceData = source.data as Record<string, unknown>;
        const activeId = isCard(sourceData) ? sourceData.id : null;
        const innermost = targets[0];
        const targetData = innermost.data;
        const targetId = isCardDropData(targetData) ? targetData.id : null;
        const edge = extractClosestEdge(targetData);

        // Over a reveal box? Resolve against THAT box's ordered member ids (their own index space); otherwise
        // against the flat canvas. One displacement Map drives both — only the ids present differ.
        const reveal = targets.map((t) => t.data as Record<string, unknown>).find(isReveal) ?? null;
        if (reveal) {
          const memberIds = reveal.memberIds;
          const fromRevealIndex = activeId ? memberIds.indexOf(activeId) : -1;
          fromRevealIndexRef.current = fromRevealIndex;
          const boxSource = fromRevealIndex === -1 ? null : fromRevealIndex; // null = incoming card opens a new slot
          // Resolve the hovered slot against THIS box's members only. The global innermost `targets[0]` can be the
          // trigger card (an ancestor wrapping the box) or a displaced member that dropped out of hit-testing — a
          // non-member hit must append, never target the trigger (which would shift it and push the drop to canvas).
          const memberTarget = pickRevealTargetCard(targets, memberIds);
          let targetIndex: number;
          if (memberTarget === null) {
            // No member under the probe. For an INCOMING card this is the box's empty tail → append at the end.
            // For a card already in THIS box (a reorder), a miss is the narrow DEAD GAP between two member cards'
            // hitboxes — appending there would yank every lower member up then snap back (flicker). Hold the last
            // member-derived target instead, exactly as the flat-canvas branch holds `flatTargetIndexRef` on a miss.
            targetIndex = boxSource === null ? memberIds.length : revealTargetIndexRef.current;
          } else if (boxSource === null) {
            targetIndex = targetIndexFromEdge(memberIds, memberTarget.id, memberTarget.edge);
          } else {
            targetIndex = getReorderDestinationIndex({
              startIndex: boxSource,
              indexOfTarget: memberIds.indexOf(memberTarget.id),
              closestEdgeOfTarget: memberTarget.edge,
              axis: 'vertical',
            });
          }
          revealTargetIndexRef.current = targetIndex;
          applyDisplacement(computeShift(memberIds, boxSource, targetIndex));
          return;
        }

        fromRevealIndexRef.current = -1;
        revealTargetIndexRef.current = 0;
        const orderedIds = orderedIdsRef.current;
        const sourceIndex = sourceIndexRef.current;
        // A container hit is an edge/gutter (above the first card, the side gutters, or past the last), not a
        // real slot — hold the last card-derived target rather than snapping to the end.
        const targetIndex =
          targetId === null
            ? flatTargetIndexRef.current
            : sourceIndex === null
              ? targetIndexFromEdge(orderedIds, targetId, edge)
              : getReorderDestinationIndex({
                  startIndex: sourceIndex,
                  indexOfTarget: orderedIds.indexOf(targetId),
                  closestEdgeOfTarget: edge,
                  axis: 'vertical',
                });
        flatTargetIndexRef.current = targetIndex;
        applyDisplacement(computeShift(orderedIds, sourceIndex, targetIndex));
      },
      onDrop({ source, location }) {
        const data = source.data as Record<string, unknown>;
        const fromFlatIndex = isCard(data) ? orderedIdsRef.current.indexOf(data.id) : -1;
        // The cycle/depth verdict for dropping this card into the box under the pointer. No box, no card, or
        // no guard supplied → allow (the verdict only gates a card entering a reveal box).
        const revealUnderPointer = location.current.dropTargets.map((t) => t.data as Record<string, unknown>).find(isReveal) ?? null;
        const canDropCardInReveal = revealUnderPointer && isCard(data) ? (guardRef.current?.(data.id, revealUnderPointer.triggerId) ?? true) : true;
        // Resolve the outcome BEFORE resetting, so the reset knows whether a commit actually happens
        // (drop-outside / same-slot → no commit → displaced cards ease back instead of snapping).
        const drop = resolveDropTarget({
          dropTargets: location.current.dropTargets,
          source: data,
          flatTargetIndex: flatTargetIndexRef.current,
          revealTargetIndex: revealTargetIndexRef.current,
          fromFlatIndex,
          fromRevealIndex: fromRevealIndexRef.current,
          canDropCardInReveal,
        });
        // Both stay synchronous in this handler, so React batches the isDropping flag and the reorder into
        // one commit — the transition suppression lands with the move.
        resetDragState(drop !== null);
        if (drop) onDropRef.current(drop);
      },
    });
    return cleanup;
  }, []);

  const contextValue = useMemo(
    () => ({ displacement, draggedId, draggedHeight, isDropping, grabOffsetYRef, draggedHeightRef, sourceIndexRef, orderedIdsRef, guardRef }),
    [displacement, draggedId, draggedHeight, isDropping],
  );

  return (
    <DndStateContext.Provider value={contextValue}>
      {children}
      {overlay &&
        createPortal(
          <div
            className="pointer-events-none fixed top-0 left-0 z-50"
            style={{ width: overlay.width, transform: `translate(${overlay.x - overlay.offsetX}px, ${overlay.y - overlay.offsetY}px)` }}>
            {overlay.content}
          </div>,
          document.body,
        )}
    </DndStateContext.Provider>
  );
}

// The grey scroll container that accepts a drop onto empty space + auto-scrolls during a drag.
export function CanvasDropZone({ children, className, onContainerClick }: { children: ReactNode; className?: string; onContainerClick?: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    // The outer scroller owns the scroll (grey gutter + overflow-auto), so the drop target and auto-scroll run
    // on it — the scrollbar sits on the grey column, and a release over the gutter still appends.
    return combine(dropTargetForElements({ element, getData: () => ({ kind: 'container' }) }), autoScrollForElements({ element }));
  }, []);
  return (
    <div ref={ref} className={cn(className, 'bg-grey-1 flex min-h-0 flex-1 flex-col overflow-auto p-4 pt-6')}>
      {/* Inner white card scrolls as content within the grey scroller; empty-area click deselects. */}
      <div
        className="flex flex-col rounded-md bg-white p-2 pb-14 shadow"
        onClick={(e) => {
          if (e.target === e.currentTarget) onContainerClick?.();
        }}>
        {children}
      </div>
    </div>
  );
}

// Grip-only drag + per-card drop target with a closest edge. The render-prop hands back the wiring so the
// card body markup stays in form-designer.tsx. The outer wrapper is the drop target and never transforms, so
// its box keeps winning `elementsFromPoint`; the slide-apart transform goes on `innerStyle`.
// `revealGroup` (triggerId:ruleId) is set when this card lives inside a reveal box, so a drop can tell a
// same-box reorder from a move between contexts; `isTrigger` marks a card that carries a rule.
type CanvasCardRootProps = { ref: Ref<HTMLDivElement> };
type CanvasCardRender = (args: {
  rootProps: CanvasCardRootProps;
  innerStyle: React.CSSProperties;
  gripRef: Ref<HTMLDivElement>;
  isDragging: boolean;
}) => ReactNode;

export function CanvasDraggableCard({
  id,
  preview,
  revealGroup,
  isTrigger,
  boxMemberIds,
  children,
}: {
  id: string;
  preview: ReactNode;
  revealGroup?: string;
  isTrigger?: boolean; // carries a rule — a reveal box refuses it (dragging it in would orphan its children)
  // Set when this card is a reveal-box member: the box's ordered member ids. Lets the probe offset use box
  // order (members aren't in the flat `orderedCardIds`), so a within-box reorder gets the same leading-edge
  // hand-over the flat canvas does instead of using the top edge and overshooting.
  boxMemberIds?: string[];
  children: CanvasCardRender;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const gripRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { displacement, draggedId, draggedHeight, isDropping, grabOffsetYRef, draggedHeightRef, sourceIndexRef, orderedIdsRef } = useContext(DndStateContext);
  // `preview` is a fresh JSX element each render; keep it in a ref so the draggable isn't torn down and
  // re-registered on every parent render (which would also unregister the active draggable mid-drag).
  const previewRef = useRef(preview);
  previewRef.current = preview;
  // Read live in getData (registered once); refs so a reorder changing the member array — or the active drag
  // id — is picked up WITHOUT re-running the effect, which would unregister the in-flight drag.
  const boxMemberIdsRef = useRef(boxMemberIds);
  boxMemberIdsRef.current = boxMemberIds;
  const draggedIdRef = useRef(draggedId);
  draggedIdRef.current = draggedId;

  useEffect(() => {
    const element = rootRef.current;
    const grip = gripRef.current;
    if (!element || !grip) return;
    return combine(
      draggable({
        element,
        dragHandle: grip,
        getInitialData: (): CardDragData => ({ kind: 'card', id, preview: previewRef.current, revealGroup, isTrigger }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
        onGenerateDragPreview: ({ nativeSetDragImage }) => disableNativeDragPreview({ nativeSetDragImage }),
      }),
      dropTargetForElements({
        element,
        // Probe with the dragged card's leading edge, not the raw pointer: a neighbour below the source hands
        // over as the dragged bottom edge crosses its midpoint, a neighbour above (and a create) as the top
        // edge does. The innermost drop target still picks the hovered card. For a within-box reorder the box's
        // member order is the relevant list (members aren't in the flat `orderedCardIds`); otherwise the flat list.
        getData: ({ input, element }) => {
          const boxIds = boxMemberIdsRef.current;
          const activeDraggedId = draggedIdRef.current;
          const inThisBox = activeDraggedId !== null && boxIds !== undefined && boxIds.includes(activeDraggedId);
          const cardIndexInList = inThisBox ? boxIds.indexOf(id) : orderedIdsRef.current.indexOf(id);
          const sourceIndexInList = inThisBox ? boxIds.indexOf(activeDraggedId) : sourceIndexRef.current;
          const probeAdjust = leadingEdgeProbeAdjust(cardIndexInList, sourceIndexInList, draggedHeightRef.current);
          const probeY = input.clientY - grabOffsetYRef.current + probeAdjust;
          return attachClosestEdge({ kind: 'card', id }, { input: { ...input, clientY: probeY }, element, allowedEdges: ['top', 'bottom'] });
        },
      }),
    );
  }, [id, revealGroup, isTrigger, grabOffsetYRef, draggedHeightRef, sourceIndexRef, orderedIdsRef]);

  const shift = displacement.get(id);
  // The dragged card holds its origin slot (visibility:hidden, in flow); displaced cards shift by one wrapper
  // height. On the drop commit (isDropping) the ease is suppressed so the transform snaps with the reorder.
  const innerStyle: React.CSSProperties =
    isDragging || draggedId === id
      ? { visibility: 'hidden', pointerEvents: 'none' }
      : {
          transform: shift ? `translateY(${shift * draggedHeight}px)` : undefined,
          // A displaced card's moved pixels must not intercept the transform-aware `elementsFromPoint`, or they
          // re-win the hit test and oscillate the gap; the untransformed wrapper stays the drop target.
          pointerEvents: shift ? 'none' : undefined,
          // The shorthand replaces the card's own `transition` class, so re-declare box-shadow / border-color
          // or the hover shadow and selected border snap instead of fading.
          transition: isDropping ? 'none' : `transform 200ms ${SETTLE_EASING}, box-shadow 300ms ease-out, border-color 300ms ease-out`,
        };
  return <>{children({ rootProps: { ref: rootRef }, innerStyle, gripRef, isDragging })}</>;
}

// The dashed box inside a trigger card that reveals fields when its rule matches. A nested
// dropTargetForElements; Pragmatic's closest-wins resolution lets drops land here over the canvas. It carries
// its ordered `memberIds` on the drop data so the monitor resolves the landing slot without any DOM geometry.
export function RevealDropZone({
  triggerId,
  ruleId,
  isEmpty,
  invalid = false,
  memberIds,
  hint,
  children,
}: {
  triggerId: string;
  ruleId: string;
  isEmpty: boolean;
  // On a save attempt with no field revealed yet, flag the box amber and emphasise the hint.
  invalid?: boolean;
  // Ordered ids currently inside this box — the landing-slot index resolves against these, and a drag of a
  // non-member is an INCOMING card that needs reserved space.
  memberIds: string[];
  hint: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isOver, setIsOver] = useState(false);
  const { draggedId, draggedHeight, guardRef } = useContext(DndStateContext);
  // getData is read at hit-test time; keep the latest member ids in a ref so a reorder inside the box doesn't
  // re-register the drop target (which would drop the active drag) just because the array identity changed.
  const memberIdsRef = useRef(memberIds);
  memberIdsRef.current = memberIds;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return dropTargetForElements({
      element: el,
      getData: (): RevealDropData => ({ kind: 'reveal', triggerId, ruleId, memberIds: memberIdsRef.current }),
      // Reject up front so this box neither lights up (onDragEnter) nor reserves a slot for a drop it can't
      // accept: a card dropped on its own trigger (self-reveal), or a cycle/over-cap nesting the guard forbids.
      canDrop: ({ source }) => canDropCardInRevealBox({ source: source.data, triggerId, guard: guardRef.current }),
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false),
    });
  }, [triggerId, ruleId, guardRef]);
  const borderClass = isOver ? 'border-default-blue' : invalid ? 'border-default-red' : 'border-grey-5';
  // Members shift via transform (visual-only — doesn't grow the box), so an INCOMING card (dragged in from
  // the toolbox or another context) would spill past the dashed border wherever the gap opens. Grow the box's
  // REAL height by one slot via bottom padding while it's hovered, so the shifted content stays inside. Keyed
  // off draggedHeight>0 (a drag is active) — NOT draggedId, which is null for a toolbox drag — and skipped for
  // a same-box reorder (the moving member's own hidden slot already holds the height).
  const dragActive = draggedHeight > 0;
  const isMemberReorder = draggedId !== null && memberIds.includes(draggedId);
  const reserveIncomingSlot = isOver && dragActive && !isMemberReorder;
  // A toolbox item's draggedHeight is its small sidebar row, not the card it becomes — floor it to a card.
  const reservedHeight = Math.max(draggedHeight, MIN_REVEAL_SLOT_HEIGHT);
  return (
    <div
      ref={ref}
      className={cn('bg-grey-1 rounded-10 flex min-h-24 flex-col gap-2 border-2 border-dashed p-5', borderClass)}
      style={reserveIncomingSlot ? { paddingBottom: `calc(1.25rem + ${reservedHeight}px)` } : undefined}>
      {isEmpty && !reserveIncomingSlot ? (
        <span className={cn('text-center text-sm', invalid ? 'text-default-red' : 'text-secondary-text')}>{hint}</span>
      ) : (
        children
      )}
    </div>
  );
}

// Toolbox item — whole-item drag (source only, no drop target). The render-prop hands back the wiring.
type ToolboxItemRender = (args: { rootRef: Ref<HTMLDivElement>; isDragging: boolean }) => ReactNode;

export function ToolboxDraggableItem({
  label,
  isDragDisabled,
  preview,
  children,
}: {
  label: string;
  isDragDisabled: boolean;
  preview: ReactNode;
  children: ToolboxItemRender;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // `preview` is a fresh JSX element each render — hold it in a ref so the draggable isn't re-registered
  // on every render. `isDragDisabled` stays in the deps because it changes the canDrag behaviour.
  const previewRef = useRef(preview);
  previewRef.current = preview;
  useEffect(() => {
    const element = rootRef.current;
    if (!element) return;
    return draggable({
      element,
      getInitialData: (): ToolboxDragData => ({ kind: 'toolbox', label, preview: previewRef.current }),
      canDrag: () => !isDragDisabled,
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
      onGenerateDragPreview: ({ nativeSetDragImage }) => disableNativeDragPreview({ nativeSetDragImage }),
    });
  }, [label, isDragDisabled]);
  return <>{children({ rootRef, isDragging })}</>;
}
